import { ConnectionService, HybridMonitorService, TargetDeviceService } from '@ids-services';
import { Component, effect, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { BaseComponent } from '@ids-components';
import { CONNECTION_TYPES, INTERFACE_TYPES } from '@ids-constants';
import { Observable, finalize, forkJoin } from 'rxjs';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { CommonModule } from '@angular/common';
import { CardComponent, CommonTableComponent, LoadingOverlayComponent, TruncateTextComponent } from '@microsec/components';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { SliderModule } from 'primeng/slider';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { ConfirmationComponent } from './confirmation/confirmation.component';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { ArrayMapPipe, ConstantPipe } from '@ids-pipes';
import { SasFormAdvancedConfigComponent } from './sas-form-advanced-config/sas-form-advanced-config.component';
import { SasIpRangeComponent } from './sas-ip-range/sas-ip-range.component';
import { PORT_OPTION_VALUES, SasPortConfigComponent } from './sas-port-config/sas-port-config.component';
import { IpHelper } from '@ids-utilities';

export const SCAN_TYPES = {
  IDS: 'ids',
  EDGE: 'edge',
};

export const IDS_FORM_PARAMS = {
  PROJECT_ID: 'project_id',
  DEVICES: 'devices',
  CONNECTION_ID: 'connection_id',
  DEVICE_ID: 'device_id',
  NETWORKS: 'networks',
  EXCLUDE_NETWORKS: 'exclude_networks',
  IP_ADDR: 'ip_addr',
  NETMASK: 'netmask',
  LEVEL_OF_AGGRESSION: 'level_of_aggression',
  SAFE: 'safe',
  FEATURES: 'features',
  TRIGGER_TYPE: 'trigger_type',
  SERVICES_TO_SCAN: 'services_to_scan',
  SCOPE: 'scope',
  SCAN_CONFIGURATIONS: 'scan_configurations',
  MAX_SCAN_RATE: 'max_scan_rate',
  MIN_SCAN_RATE: 'min_scan_rate',
  DELAY_TIME: 'delay_time',
  TIMEOUT: 'timeout',
  MAX_RETRY_NUMBER: 'max_retry_number',
  SCAN_COMMANDS: 'scan_commands',
  PORT_OPTIONS: 'port_options',
  PORTS_TO_SCAN: 'ports_to_scan',
  CUSTOM_PORTS: 'custom_ports',
  PORT: 'port',
  SERVICE: 'service',
};

const EDGE_FORM_PARAMS = {
  COMMAND: 'command',
  ACTIVE_SCAN: 'active_scan',
  RUN_SCAN: 'run_scan',
  SCAN_MODE: 'scan_mode',
};

const AGRESSION_LEVEL_INDEX: { [key: number]: string } = {
  1: 'safe',
  2: 'advanced',
  3: 'intensive',
};

export const IP_ADDRESS_PATTERN = '(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)';

const ADVANCED_CONFIG_DEFAULT_VALUES = {
  [IDS_FORM_PARAMS.MAX_SCAN_RATE]: 2000,
  [IDS_FORM_PARAMS.MIN_SCAN_RATE]: 1000,
  [IDS_FORM_PARAMS.DELAY_TIME]: 100,
  [IDS_FORM_PARAMS.TIMEOUT]: 1000,
  [IDS_FORM_PARAMS.MAX_RETRY_NUMBER]: 1,
  [IDS_FORM_PARAMS.SCAN_COMMANDS]: '',
};

@Component({
  selector: 'app-shared-active-scan-form',
  templateUrl: './shared-active-scan-form.component.html',
  styleUrls: ['./shared-active-scan-form.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TabsModule,
    TooltipModule,
    ButtonModule,
    ReactiveFormsModule,
    RadioButtonModule,
    SliderModule,
    CheckboxModule,
    SelectModule,
    LoadingOverlayComponent,
    CardComponent,
    CommonTableComponent,
    TruncateTextComponent,
    ConfirmationComponent,
    ArrayMapPipe,
    ConstantPipe,
    SasFormAdvancedConfigComponent,
    SasIpRangeComponent,
    SasPortConfigComponent,
  ],
  providers: [ArrayMapPipe, ConstantPipe],
})
export class SharedActiveScanFormComponent extends BaseComponent implements OnInit {
  isLoading = false;

  step: WritableSignal<string> = signal('');

  scanType: WritableSignal<string> = signal('');

  connections: any[] = [];

  devices: any[] = [];

  scopeOptions = [
    { value: 'network', label: 'Existing Network', description: 'Only enabled physical connection are supported.' },
    { value: 'devices', label: 'Detected Devices', description: 'Only devices from enabled physical connection are supported.' },
    { value: 'ipRange', label: 'IP Range', description: 'Select a custom IP range to scan.' },
  ];

  idsConnectionOptions: any[] = [];

  idsDevices: any[] = [];

  selectedIdsDevices: any[] = [];

  selectedEdgeDevices: any[] = [];

  deviceCols = [
    { field: 'label', header: 'Name', width: 15 },
    { field: 'connection', header: 'Connection', width: 15 },
    { field: 'type', header: 'Type', width: 15 },
    { field: 'zones', header: 'Zones', width: 15 },
  ];

  selectedAggressionLevel = 1;

  aggressionLevels: any = {};

  servicePorts: any[] = [];

  form!: FormGroup;

  customPortDefaultValues: any[] = [];

  SCAN_TYPES = SCAN_TYPES;

  IDS_FORM_PARAMS = IDS_FORM_PARAMS;

  activeTabIndex = 0;

  @ViewChild(SasIpRangeComponent) ipRangeComponent!: SasFormAdvancedConfigComponent;

  @ViewChild(SasPortConfigComponent) portConfigComponent!: SasPortConfigComponent;

  @ViewChild(SasFormAdvancedConfigComponent) advConfigFormComponent!: SasFormAdvancedConfigComponent;

  constructor(
    public dialogRef: DynamicDialogRef,
    private connectionSrv: ConnectionService,
    private hybridMonitorSrv: HybridMonitorService,
    private targetDeviceSrv: TargetDeviceService,
  ) {
    super();
    effect(() => {
      const step = this.step();
      const scanType = this.scanType();
      switch (scanType) {
        case SCAN_TYPES.IDS: {
          this.activeTabIndex = 0;
          if (step === 'form') {
            this.getData();
          }
          break;
        }
        default: {
          this.activeTabIndex = 1;
          break;
        }
      }
    });
  }

  async ngOnInit() {
    await this.prepareConfigs();
    this.initForm();
    this.step.set('confirmation');
    this.scanType.set(SCAN_TYPES.IDS);
  }

  initForm() {
    this.form = new FormGroup({
      [IDS_FORM_PARAMS.SCOPE]: new FormControl('network'),
      [IDS_FORM_PARAMS.CONNECTION_ID]: new FormControl(null, Validators.required),
    });
    setTimeout(() => {
      // Listen for value changes
      this.form?.get(IDS_FORM_PARAMS.SCOPE)?.valueChanges?.subscribe((value) => {
        this.form?.get(IDS_FORM_PARAMS.CONNECTION_ID)?.setValidators(value === 'network' ? Validators.required : Validators.nullValidator);
        this.form?.get(IDS_FORM_PARAMS.CONNECTION_ID)?.updateValueAndValidity();
        if (value === 'ipRange') {
          setTimeout(() => {
            this.addIpRangeControls();
            this.addCustomPortControls();
          });
        } else {
          this.removeIpRangeControls();
        }
      });
    });
  }

  removeIpRangeControls() {
    this.form.removeControl(IDS_FORM_PARAMS.NETWORKS);
    this.form.removeControl(IDS_FORM_PARAMS.EXCLUDE_NETWORKS);
    this.form.removeControl(IDS_FORM_PARAMS.PORT_OPTIONS);
    this.form.removeControl(IDS_FORM_PARAMS.PORTS_TO_SCAN);
    this.form.removeControl(IDS_FORM_PARAMS.CUSTOM_PORTS);
  }

  async getData() {
    await this.prepareConfigs();
    this.isLoading = true;
    forkJoin({
      connections: this.connectionSrv.getConnections(this.breadcrumbConfig?.organizationId, this.breadcrumbConfig?.projectId),
      capabilities: this.hybridMonitorSrv.getCapabilities(),
      devices: this.targetDeviceSrv.getDevices({
        organizationId: this.breadcrumbConfig?.organizationId,
        projectId: this.breadcrumbConfig?.projectId,
        detailed: false,
      }),
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.setIdsData();
        }),
      )
      .subscribe({
        next: (res) => {
          this.connections = (res?.connections?.data as any[]) || [];

          this.aggressionLevels = res?.capabilities?.level_of_aggression || {};
          this.servicePorts = (res?.capabilities?.default_ports || []).map((s: any) => ({
            [IDS_FORM_PARAMS.SERVICE]: s.service,
            [IDS_FORM_PARAMS.PORT]: s.port,
          }));

          this.devices = (res?.devices?.devices as any[]) || [];
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }

  // Add IP Range Selection Child Form to Parent Form
  addIpRangeControls() {
    const IpRangeForm = this.ipRangeComponent?.form;

    if (IpRangeForm) {
      this.form.addControl(IDS_FORM_PARAMS.NETWORKS, IpRangeForm.getControl(IDS_FORM_PARAMS.NETWORKS));
      this.form.addControl(IDS_FORM_PARAMS.EXCLUDE_NETWORKS, IpRangeForm.getControl(IDS_FORM_PARAMS.EXCLUDE_NETWORKS));
    }
  }

  // Add Custom Port Child Form to Parent Form
  addCustomPortControls() {
    const customPortForm = this.portConfigComponent?.form;

    if (customPortForm) {
      this.form.addControl(IDS_FORM_PARAMS.PORT_OPTIONS, customPortForm.getControl(IDS_FORM_PARAMS.PORT_OPTIONS));
      this.form.addControl(IDS_FORM_PARAMS.PORTS_TO_SCAN, customPortForm.getControl(IDS_FORM_PARAMS.PORTS_TO_SCAN));
      this.form.addControl(IDS_FORM_PARAMS.CUSTOM_PORTS, customPortForm.getControl(IDS_FORM_PARAMS.CUSTOM_PORTS));
    }
  }

  setIdsData() {
    this.idsConnectionOptions = this.connections
      .filter((c) => c.interface?.type === CONNECTION_TYPES.PHYSICAL && !!c.enabled)
      .map((c) => ({ value: c.id, label: c.name }));

    const connectionIds = (this.idsConnectionOptions || []).map((c) => c.value);
    const devices = this.devices
      .filter(
        (d) => !!Object.keys(d[INTERFACE_TYPES.IP] || {}).length && ((d.connection_ids || []) as any[]).some((id) => connectionIds.includes(id)),
      )
      .map((d) => ({
        ...d,
        connectionOptions: this.connections
          .filter((c) => ((d.connection_ids as any[]) || []).includes(c.id))
          .map((c) => ({
            label: c.name,
            value: c.id,
            type: c.interface?.type,
          })),
      }))
      .map((d) => ({ ...d, defaultConnectionId: d.connectionOptions[0]?.value }));
    this.idsDevices = devices;

    this.form?.get(IDS_FORM_PARAMS.CONNECTION_ID)?.setValue(this.idsConnectionOptions?.[0]?.value || null);
    this.form?.get(IDS_FORM_PARAMS.CONNECTION_ID)?.updateValueAndValidity();
  }

  onSubmit() {
    if (this.scanType() === SCAN_TYPES.IDS) {
      this.startIdsActiveScan();
    } else {
      this.startEdgeActiveScan();
    }
  }

  startIdsActiveScan() {
    const values = this.form.getRawValue();
    const scope = values[IDS_FORM_PARAMS.SCOPE];
    if (scope === 'ipRange') {
      const excludeNetworkError = IpHelper.hasIpExcludeError(values[IDS_FORM_PARAMS.NETWORKS], values[IDS_FORM_PARAMS.EXCLUDE_NETWORKS]);
      if (!!excludeNetworkError) {
        const errMsg = 'Excluded IP out of bound';
        this.form.get(IDS_FORM_PARAMS.EXCLUDE_NETWORKS)?.setErrors({ incorrect: true, message: errMsg });
        this.showErrorMessage(excludeNetworkError?.message || errMsg);
        return;
      }
    }
    const networks = this.IpSubnetConversion(values[IDS_FORM_PARAMS.NETWORKS]);
    const excludedNetworks = this.IpSubnetConversion(values[IDS_FORM_PARAMS.EXCLUDE_NETWORKS]);
    const advConfigValues = this.advConfigFormComponent.form?.getRawValue();
    const payload = {
      [IDS_FORM_PARAMS.PROJECT_ID]: this.breadcrumbConfig?.projectId,
      ...(scope !== 'ipRange'
        ? {
            [IDS_FORM_PARAMS.DEVICES]: [
              ...(values[IDS_FORM_PARAMS.SCOPE] === 'devices'
                ? this.selectedIdsDevices?.map((device) => ({
                    [IDS_FORM_PARAMS.DEVICE_ID]: device.id,
                    [IDS_FORM_PARAMS.CONNECTION_ID]: device.defaultConnectionId,
                  })) || []
                : [
                    {
                      [IDS_FORM_PARAMS.CONNECTION_ID]: values[IDS_FORM_PARAMS.CONNECTION_ID],
                    },
                  ]),
            ],
          }
        : {
            [IDS_FORM_PARAMS.NETWORKS]: [...networks],
            [IDS_FORM_PARAMS.EXCLUDE_NETWORKS]: [...excludedNetworks],
          }),
      [IDS_FORM_PARAMS.LEVEL_OF_AGGRESSION]: {
        [AGRESSION_LEVEL_INDEX[this.selectedAggressionLevel]]: this.aggressionLevels[AGRESSION_LEVEL_INDEX[this.selectedAggressionLevel]] || {},
      },
      [IDS_FORM_PARAMS.SCAN_CONFIGURATIONS]: !!advConfigValues ? { ...advConfigValues } : { ...ADVANCED_CONFIG_DEFAULT_VALUES },
      [IDS_FORM_PARAMS.CUSTOM_PORTS]: values[IDS_FORM_PARAMS.CUSTOM_PORTS],
      [IDS_FORM_PARAMS.PORTS_TO_SCAN]:
        values[IDS_FORM_PARAMS.PORT_OPTIONS] === PORT_OPTION_VALUES.SPECIFIC_PORTS ? values[IDS_FORM_PARAMS.PORTS_TO_SCAN] : [],
    };

    this.isLoading = true;
    this.hybridMonitorSrv
      .startActiveScan(payload)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (rs) => {
          this.dialogRef?.close({ scanType: SCAN_TYPES.IDS, data: rs });
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }

  startEdgeActiveScan() {
    const payload = {
      [EDGE_FORM_PARAMS.COMMAND]: {
        [EDGE_FORM_PARAMS.ACTIVE_SCAN]: {
          [EDGE_FORM_PARAMS.RUN_SCAN]: 'true',
          [EDGE_FORM_PARAMS.SCAN_MODE]: AGRESSION_LEVEL_INDEX[this.selectedAggressionLevel],
        },
      },
    };
    const requests: Observable<any>[] = this.selectedEdgeDevices.map((d) => this.targetDeviceSrv.sendDeviceCommand(d.id, payload));
    forkJoin(requests)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (rs) => {
          this.dialogRef?.close({ scanType: SCAN_TYPES.EDGE, data: rs });
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }

  onDeviceConnectionChange(device: any) {
    const index = this.selectedIdsDevices?.findIndex((selectedDevice) => selectedDevice.id === device.id) || -1;
    if (index > -1) {
      this.selectedIdsDevices[index] = device;
    }
  }

  markFieldAsTouchedAndDirty(formName: string) {
    this.form?.get(formName)?.markAsTouched();
    this.form?.get(formName)?.markAsDirty();
  }

  onCancel() {
    this.dialogRef.close();
  }

  onConfirm() {
    this.step.set('form');
    if (this.scanType() === SCAN_TYPES.EDGE) {
      this.selectedAggressionLevel = 1;
    }
  }

  resetConfigValues() {
    this.isLoading = true;
    setTimeout(() => {
      switch (this.activeTabIndex) {
        case 1: {
          this.advConfigFormComponent.form?.patchValue(ADVANCED_CONFIG_DEFAULT_VALUES);
          break;
        }
        case 2: {
          this.portConfigComponent.form?.patchValue({ [IDS_FORM_PARAMS.CUSTOM_PORTS]: this.servicePorts });
          break;
        }
        default: {
          this.showErrorMessage('Reset To Default is not available on this form');
          break;
        }
      }
      this.isLoading = false;
    });
  }

  get isValid() {
    return this.scanType() === SCAN_TYPES.IDS
      ? !(this.form.invalid || (this.form.get(IDS_FORM_PARAMS.SCOPE)?.value === 'devices' && !this.selectedIdsDevices.length))
      : !!this.selectedEdgeDevices.length && (this.selectedAggressionLevel === 1 || this.selectedAggressionLevel === 2);
  }

  get isChildAdvConfigShown(): boolean {
    return this.advConfigFormComponent?.showConfig;
  }

  get isChildCustomPortConfigShown(): boolean {
    return this.portConfigComponent?.showConfig;
  }

  // ==================================== HELPERS ====================================

  // Convert IP range or individual IP in an array to objects of ip_addr and subnet_mask
  IpSubnetConversion(networks: string[]) {
    return ((networks as any[]) || []).map((network: string) => {
      const netmaskIndex = network.lastIndexOf('/');

      // Input of IP range like 1.1.1.1/24
      if (netmaskIndex !== -1) {
        return {
          [IDS_FORM_PARAMS.IP_ADDR]: network.slice(0, netmaskIndex),
          [IDS_FORM_PARAMS.NETMASK]: Number(network.slice(netmaskIndex + 1)) || 32,
        };
      }

      // Input of individual IP like 1.1.1.1
      return { [IDS_FORM_PARAMS.IP_ADDR]: network, [IDS_FORM_PARAMS.NETMASK]: 32 };
    });
  }
}
